import { BrandConfigSchema, BrandConfigError, type BrandConfig } from './types.js';

let cached: BrandConfig | undefined;

/**
 * Build-time / bundler-friendly brand config loader.
 *
 * Consumers (Vite, esbuild, Node ≥ 20.10 with --experimental-json-modules) can import
 * `/brand/brand.config.json` as a module. We pass that through Zod validation here so
 * a malformed config fails loudly at startup instead of silently producing an `undefined`.
 *
 * Pass an explicit `raw` object to override the default import — used by tests and the
 * rebrand smoke test.
 */
export function getBrandConfig(raw?: unknown): BrandConfig {
  if (cached && raw === undefined) return cached;
  const source = raw ?? loadDefault();
  const parsed = BrandConfigSchema.safeParse(source);
  if (!parsed.success) {
    throw new BrandConfigError(
      `Invalid brand.config.json: ${parsed.error.issues
        .map((i: { path: (string | number)[]; message: string }) => `${i.path.join('.')}: ${i.message}`)
        .join('; ')}`,
    );
  }
  if (raw === undefined) cached = parsed.data;
  return parsed.data;
}

/** Resets the cached config. Used by tests. */
export function resetBrandConfigCache(): void {
  cached = undefined;
}

function loadDefault(): unknown {
  // Resolved at build time by the bundler. In Vite this is a static JSON import.
  // For Node-only consumers, prefer `@erp/shared/brand/node` which reads from disk.
  // The dynamic import keeps this file usable in environments where /brand isn't
  // bundle-resolvable (e.g. unit tests that always pass `raw`).
  // Throwing here is intentional — callers should provide `raw` in non-bundler envs.
  throw new BrandConfigError(
    'No brand config provided. Pass a parsed object as `raw`, ' +
      'or import from `@erp/shared/brand/node` to read from disk.',
  );
}
